import React, { useMemo, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import { orderBy } from 'lodash';
import moment from 'moment';
import { RejectionDetailsCard } from 'components/pages/collection';
import { Box, Flex, Text } from 'atoms';
import { theme } from 'styles/theme';
import { useQueryParams } from 'hooks';
import { Rupee, Shimmer } from 'components';
import * as SVGICONS from 'utils/svg';
import {
	getStoreDetails,
	setFilteredInvoices,
	setSalesMenSearch,
	getAlreadyPaidUPIList,
	getDeliveryDate,
	getBanks,
	getReasons,
} from 'store/salesmanSlice';
import { InvoiceAmortization } from 'components/pages/collection-app/invoice-amortization';
import SalesHeaderCollection from 'components/pages/collection-app/SalesHeaderCollection';

const Container = styled(Flex)`
	flex-direction: column;
	background-color: ${(props) => props.theme.colors.white};
`;
const SegContainer = styled(Box)`
	background: #f3fd51;
	padding: 15px;
	font-size: 14px;
	line-height: 1.2;
	font-weight: 500;
	text-align: center;
`;
const CashierContainer = styled(Box)`
	background: #f3fd51;
	padding: 15px;
	font-size: 14px;
	line-height: 1.2;
	font-weight: 500;
	display: flex;
	text-align: center;
`;

export default function OutletPayment() {
	const { invoiceDate } = useSelector((state) => state.app);
	const { me = {} } = useSelector((state) => state.user);
	const [showShimmer, setShowShimmer] = useState(true);
	const [isEditMode, setIsEditMode] = useState(false);
	const { storeId } = useQueryParams();
	const dispatch = useDispatch();
	const [previousUPIPayments, setPreviousUPIPayments] = useState();
	const [display_rejected, setdisplay_rejected] = useState(0);

	const { salesmanId = undefined } = useQueryParams();

	const user_info = JSON.parse(localStorage.getItem('collectionApp'));

	const {
		SalesInvoices = [],
		store_data,
		can_salesman_edit,
	} = useSelector((state) => state.salesman);

	useEffect(() => {
		if (invoiceDate && storeId) {
			dispatch(getStoreDetails(storeId));
		}
		dispatch(getBanks());
		dispatch(getReasons());
	}, [dispatch, invoiceDate, storeId]);

	const sameInvStore = useMemo(() => {
		let sortedInvoices = store_data?.invoices;

		sortedInvoices = orderBy(
			sortedInvoices,

			(o) => moment(o.invoice_date).format('YYYY-MM-DD hh:mm:ss'),

			['asc']
		);

		return sortedInvoices;
	}, [store_data]);

	useEffect(() => {
		if (sameInvStore?.length > 0) {
			setTimeout(() => {
				setShowShimmer(false);
			}, 3000);
		}
	}, [sameInvStore?.length]);

	useEffect(() => {
		if (store_data?.payments?.length > 0) {
			setIsEditMode(true);
		} else {
			setIsEditMode(false);
		}
	}, [dispatch, store_data]);

	function resetFilter() {
		/**
		 * on page load and unload reset the filters
		 */
		dispatch(setFilteredInvoices(SalesInvoices));
		dispatch(setSalesMenSearch(''));
	}

	const [showgetDelDetails, SetshowgetDelDetails] = useState({});

	useEffect(() => {
		dispatch(getDeliveryDate()).then((res) => {
			SetshowgetDelDetails(res);
		});
	}, [dispatch]);

	useEffect(() => {
		dispatch(
			getAlreadyPaidUPIList({
				salesmanId: salesmanId || me?.id || user_info?.id,
				store_id: storeId,
			})
		).then((res) => {
			setPreviousUPIPayments(res?.data);
		});
	}, [dispatch, me, salesmanId, storeId, user_info?.id]);

	useEffect(() => {
		resetFilter();

		return () => {
			resetFilter();
		};
	}, []);

	useEffect(() => {
		const rejecte_list = ['RejectedByCashier', 'RejectedBySegregator'];

		setdisplay_rejected(
			sameInvStore?.filter((item) => rejecte_list.includes(item?.invoice_verification_status))
				.length
		);
	}, [display_rejected, sameInvStore]);

	const totalOutstanding = sameInvStore?.reduce(
		(acc, curr) => acc + Number(curr?.current_outstanding_amount) || 0,
		0
	);
	const initialOutstanding = sameInvStore?.reduce(
		(acc, curr) => acc + Number(curr?.initial_outstanding_amount) || 0,
		0
	);
	const checkRejectedStatus = store_data?.payments?.filter(
		(item) => item?.payment_verification_status === 'RejectedByCashier'
	);

	return (
		<div>
			{showShimmer ? (
				<Shimmer />
			) : (
				<Container>
					<SalesHeaderCollection />
					{store_data?.store_info?.segaction && checkRejectedStatus.length === 0 && (
						<SegContainer>
							Further modifications or additions are not permitted, as all invoices
							have been verified and finalized by the Segregator
						</SegContainer>
					)}
					{/* {store_data?.store_info?.can_salesman_edit === false && (
						<SegContainer>
							Verification in progress. Once complete, you can proceed with
							corrections.
						</SegContainer>
					)} */}

					{display_rejected > 0 && (
						<CashierContainer>
							<SVGICONS.RejecctionSVG />
							<Box style={{ paddingTop: '10px' }}>
								Some payment details were rejected, please correct them
							</Box>
						</CashierContainer>
					)}

					<Text
						textAlign="center"
						color={theme.colors.primary}
						fontSize="10px"
						mt="10px"
						textTransform="capitalize"
					>
						{store_data?.store_info?.store_name}
					</Text>
					<Flex my="10px" justifyContent="center">
						<Text textAlign="center" fontSize="10px" mx="5px">
							Total Invoice:{' '}
							<Text as="span" fontWeight="bold">
								{store_data?.invoices?.length}
							</Text>
						</Text>
						<Text textAlign="center" fontSize="10px" mx="5px">
							Total Outstanding Value:{' '}
							<Text as="span" fontWeight="bold">
								<Rupee>{totalOutstanding}</Rupee>
							</Text>
						</Text>
					</Flex>
					<RejectionDetailsCard
						SalesInvoices={sameInvStore?.filter(
							(item) => item?.rejectNotification?.length
						)}
					/>
					<InvoiceAmortization
						isEditMode={isEditMode}
						storeInvoices={sameInvStore}
						totalOutstanding={totalOutstanding}
						store_data={store_data}
						previousUPIPayments={previousUPIPayments}
						deliveryDate={showgetDelDetails}
						can_salesman_edit={can_salesman_edit}
						outstandingAmount={initialOutstanding}
					/>
				</Container>
			)}
		</div>
	);
}
